import React from 'react';
import ReactDOM from 'react-dom/client';

import * as API from '../API';

import ConsolidateAndResend from '../Components/ConsolidateAndResend/ConsolidateAndResend';

import { InvoiceModelMapper } from '@/Components/OrderNomenclatureGrid/InvoiceModelMapper';
import 'dayjs/locale/en-gb';
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import AutoInsertTextBlocks from '../Components/AutoInsertTextBlocks/AutoInsertTextBlocks';
import PreviousInvoicingBatches from '../Components/Invoicing/PreviousInvoicingBatches';
import InvoiceRemarksGrid from '../Components/OrderNomenclatureGrid/InvoiceRemarksGrid';
import OrderNomenclatureGrid from '../Components/OrderNomenclatureGrid/OrderNomenclatureGrid';
import PendingBatches from '../Components/PendingBatches/PendingBatches';
import PhysicianIdentifiers from '../Components/PhysicianIdentifiers/PhysicianIdentifiers';
import ReportTextBlocks from '../Components/ReportTextBlocks/ReportTextBlocks';

const setup = async (): Promise<any> => {
    return await API.general.getTranslations()
        .then(async (translations) => { // [{key: '', value: ''}, ...]
            const t: any = {};
            translations.map((x: { Key: string; Value: string }) => {
                t[x.Key] = x.Value;
                return t[x.Key];
            });
            return await i18n.use(initReactI18next)
                .init({
                    resources: {
                        xx: {
                            translation: t,
                        },
                    },
                    lng: 'xx', // NOTE: Language doesn't match because it is dynamically gotten from the server using API.getTranslations
                });
        })
        .catch((err) => console.error('Could not get translations:', err));
};

const loadConsolidateAndResend = (): void => {
    void setup()
        .then((_) =>
            ReactDOM.createRoot(document.getElementById('consolidateAndResendRoot')!).render(
                <React.StrictMode>
                    <ConsolidateAndResend />
                </React.StrictMode>,
            ),
        );
};

const loadPendingBatches = (): void => {
    void setup()
        .then((_) =>
            ReactDOM.createRoot(document.getElementById('pendingEFactBatchesRoot')!).render(
                <React.StrictMode>
                    <PendingBatches />
                </React.StrictMode>,
            ),
        );
};

const loadReportTextBlocks = (): void => {
    void setup()
        .then((_) => {
            const orderProcedureList = document.getElementById('procInReport');
            const procList = document.getElementById('proceduresInReporContainer');
            const procH2Overlap = document.getElementById('proceduresH2Overlap');
            // Yeah, this is hacky, don't see a better way for now...
            // @ts-ignore
            const procedureIDs = Array.from((orderProcedureList ?? procList ?? procH2Overlap)?.querySelectorAll('.hidden.number') ?? []).map((x) => x.innerText * 1);

            ReactDOM.createRoot(document.getElementById('reportTextBlocksOverview')!).render(
                <React.StrictMode>
                    <ReportTextBlocks procedureIDs={procedureIDs} />
                </React.StrictMode>,
            );
        });
};

const loadAutoInsertTextBlocks = (textBlocks: any[], onClick: Function, onCancel: Function): void => {
    void setup()
        .then((_) => {
            ReactDOM.createRoot(document.getElementById('textBlockOptions')!).render(
                <React.StrictMode>
                    <AutoInsertTextBlocks textBlocks={textBlocks} onClick={onClick} onCancel={onCancel} />
                </React.StrictMode>,
            );
        });
};

const loadPreviousInvoicingBatches = (): void => {
    void setup()
        .then((_) =>
            ReactDOM.createRoot(document.getElementById('previousInvoicingBatchesRoot')!).render(
                <React.StrictMode>
                    <PreviousInvoicingBatches />
                </React.StrictMode>,
            ),
        );
};

const loadOrderNomenclatureGrid = (uniquePrefix: string, getInvoiceInformation: Function): void => {
    void setup()
        .then((_) => {
            if (!document.getElementById('orderNomencaltureGrid')) {
                return;
            }

            const invoiceModelKey = 'OrderNomenclatureGridInvoiceModel' + uniquePrefix;
            const procedureItemsKey = 'OrderNomenclatureGridListProcedureItems' + uniquePrefix;

            const invoiceModel = JSON.parse(sessionStorage.getItem(invoiceModelKey) ?? '');
            const procedures = JSON.parse(sessionStorage.getItem(procedureItemsKey) ?? '');

            const mappedInvoiceModel = new InvoiceModelMapper().map(invoiceModel);

            const updateInvoiceModel = (): void => {
                sessionStorage.setItem('OrderNomenclatureGridInvoiceModel' + uniquePrefix, JSON.stringify(mappedInvoiceModel));
            };

            ReactDOM.createRoot(document.getElementById('orderNomencaltureGrid')!).render(
                <React.StrictMode>
                    <OrderNomenclatureGrid invoiceModel={mappedInvoiceModel} procedures={procedures} getInvoiceInformation={getInvoiceInformation} updateInvoiceModel={updateInvoiceModel} />
                </React.StrictMode>,
            );
        });
};

const loadInvoiceRemarks = (uniquePrefix: string): void => {
    void setup()
        .then((_) => {
            if (!sessionStorage.getItem('OrderNomenclatureGridInvoiceModel' + uniquePrefix) || !document.getElementById('orderInvoiceRemarks')) {
                return;
            }
            const invoiceModel = JSON.parse(sessionStorage.getItem('OrderNomenclatureGridInvoiceModel' + uniquePrefix) ?? '');
            const mappedInvoiceModel = new InvoiceModelMapper().map(invoiceModel);
            ReactDOM.createRoot(document.getElementById('orderInvoiceRemarks')!).render(
                <React.StrictMode>
                    <InvoiceRemarksGrid invoiceModel={mappedInvoiceModel} />
                </React.StrictMode>,
            );
        });
};

const loadPhysicianIdentifiers = (physicianID: number, isEdit: boolean): void => {
    void setup()
        .then((_) => {
            ReactDOM.createRoot(document.getElementById('physicianIdentifiersRoot')!).render(
                <React.StrictMode>
                    <PhysicianIdentifiers physicianID={physicianID} isEdit={isEdit} />
                </React.StrictMode>,
            );
        });
};

export {
    loadConsolidateAndResend,
    loadPendingBatches,
    loadReportTextBlocks,
    loadAutoInsertTextBlocks,
    loadPreviousInvoicingBatches,
    loadOrderNomenclatureGrid,
    loadInvoiceRemarks,
    loadPhysicianIdentifiers,
};
